import React, { useState, useEffect, useRef } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Spin } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  InboxOutlined,
  UserOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ShopOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  FolderOutlined,
  FileSearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Header, Sider, Content } = AntLayout;

const getDefaultHeaderTitle = (pathname: string): string | null => {
  if (pathname === '/' || pathname === '/dashboard') return 'Dashboard';
  if (pathname.startsWith('/calendar')) return 'Calendar';
  if (pathname.startsWith('/files')) return 'Files';
  if (pathname.startsWith('/customers')) return 'Personal';
  if (pathname.startsWith('/businesses')) return 'Business';
  if (pathname.startsWith('/reports')) return 'Reports';
  if (pathname.startsWith('/tasks')) return 'Tasks';
  if (pathname.startsWith('/letters')) return 'Letters';
  if (pathname.startsWith('/dispatches')) return 'Dispatches';
  if (pathname.startsWith('/quotations')) return 'Quotations';
  if (pathname.startsWith('/invoices')) return 'Invoices';
  if (pathname.startsWith('/task-templates')) return 'Task Templates';
  if (pathname.startsWith('/settings')) return 'Settings';
  return null;
};

export const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [headerContent, setHeaderContent] = useState<React.ReactNode>(null);
  const listingHeaderObserversRef = useRef<ResizeObserver[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading, isAuthenticated, refreshUser } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
  }, [isAuthenticated, location.pathname, refreshUser]);

  useEffect(() => {
    setHeaderContent(null);
  }, [location.pathname]);

  useEffect(() => {
    let scheduled = false;

    const setupListingHeaderObservers = () => {
      listingHeaderObserversRef.current.forEach((observer) => observer.disconnect());
      listingHeaderObserversRef.current = [];

      const tables = Array.from(document.querySelectorAll('.flowbite-listing-table'));
      tables.forEach((table) => {
        const headerRow = table.querySelector('thead tr:first-child');
        const headerGroup = table.querySelector('thead') as HTMLElement | null;
        const scrollContainer = table.closest('.flowbite-listing-table-scroll') as HTMLElement | null;
        if (!headerRow) {
          return;
        }
        const updateHeight = () => {
          const firstRowHeight = headerRow.getBoundingClientRect().height;
          const headerHeight = headerGroup?.getBoundingClientRect().height ?? 0;
          if (firstRowHeight) {
            (table as HTMLElement).style.setProperty('--listing-header-row-height', `${Math.ceil(firstRowHeight)}px`);
          }
          if (scrollContainer && headerHeight) {
            scrollContainer.style.setProperty('--listing-header-height', `${Math.ceil(headerHeight)}px`);
          }
        };
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(headerRow);
        if (headerGroup && headerGroup !== headerRow) {
          observer.observe(headerGroup);
        }
        listingHeaderObserversRef.current.push(observer);
      });
    };

    const scheduleSetup = () => {
      if (scheduled) {
        return;
      }
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        setupListingHeaderObservers();
      });
    };

    setupListingHeaderObservers();

    const mutationObserver = new MutationObserver(scheduleSetup);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      listingHeaderObserversRef.current.forEach((observer) => observer.disconnect());
      listingHeaderObserversRef.current = [];
    };
  }, [location.pathname]);

  const noContentScrollRoutes = [
    '/customers',
    '/businesses',
    '/tasks',
    '/letters',
    '/dispatches',
    '/quotations',
    '/invoices',
    '/task-templates',
  ];
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const disableContentScroll = noContentScrollRoutes.includes(normalizedPath);

  useEffect(() => {
    if (!disableContentScroll) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [disableContentScroll]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const hasPermission = (permission: string) => {
    if (user?.roles?.some((role) => ['owner', 'admin', 'developer'].includes(role))) {
      return true;
    }
    return user?.permissions?.includes(permission) ?? false;
  };

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      visible: true,
    },
    {
      key: '/calendar',
      icon: <CalendarOutlined />,
      label: 'Calendar',
      visible: hasPermission('calendar.view'),
    },
    {
      key: '/files',
      icon: <FolderOutlined />,
      label: 'Files',
      visible: hasPermission('files.view'),
    },
    {
      type: 'group' as const,
      label: 'Customer',
      children: [
        {
          key: '/customers',
          icon: <UserOutlined />,
          label: 'Personal',
          visible: hasPermission('customer.personal.view'),
        },
        {
          key: '/businesses',
          icon: <ShopOutlined />,
          label: 'Business',
          visible: hasPermission('customer.business.view'),
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Project',
      children: [
        {
          key: '/reports',
          icon: <FileSearchOutlined />,
          label: 'Reports',
          visible: hasPermission('projects.reports.view'),
        },
        {
          key: '/tasks',
          icon: <CheckSquareOutlined />,
          label: 'Tasks',
          visible: hasPermission('projects.tasks.view'),
        },
        {
          key: '/letters',
          icon: <FileTextOutlined />,
          label: 'Letters',
          visible: hasPermission('projects.letters.view'),
        },
        {
          key: '/dispatches',
          icon: <InboxOutlined />,
          label: 'Dispatches',
          visible: hasPermission('projects.dispatch.view'),
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Billing',
      children: [
        {
          key: '/quotations',
          icon: <FileTextOutlined />,
          label: 'Quotations',
          visible: hasPermission('billing.quotations.view'),
        },
        {
          key: '/invoices',
          icon: <FileTextOutlined />,
          label: 'Invoices',
          visible: hasPermission('billing.invoices.view'),
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Workspace',
      children: [
        {
          key: '/task-templates',
          icon: <FileTextOutlined />,
          label: 'Task Templates',
          visible: hasPermission('task_template.view'),
        },
      ],
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      visible: hasPermission('settings.view') || hasPermission('settings.update'),
    },
  ]
    .map((item) => {
      if (!item.children) {
        return item;
      }
      const visibleChildren = item.children.filter((child) => (child as any).visible !== false);
      return { ...item, children: visibleChildren };
    })
    .filter((item) => {
      if ((item as any).visible === false) {
        return false;
      }
      if (item.children && item.children.length === 0) {
        return false;
      }
      return true;
    });

  const userMenuItems = [
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: logout,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const menuKeys = menuItems.flatMap((item: any) => {
    if (item.children) {
      return item.children.map((child: any) => child.key).filter(Boolean);
    }
    return item.key ? [item.key] : [];
  });

  const selectedMenuKey =
    menuKeys
      .filter((key) => location.pathname === key || location.pathname.startsWith(`${key}/`))
      .sort((a, b) => b.length - a.length)[0] || location.pathname;

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible
        collapsed={collapsed}
        collapsedWidth={80}
        className="app-sider"
        width={240}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <div
          className="app-sider-logo"
          style={{ 
          height: 32, 
          margin: 16, 
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600
        }}>
          {collapsed ? 'BFS' : 'Byte Flow Soution'}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedMenuKey]}
          items={menuItems}
          onClick={handleMenuClick}
          className="app-menu"
        />
      </Sider>
      <AntLayout style={{ marginLeft: collapsed ? 80 : 240 }}>
        <Header style={{ 
          padding: 0, 
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: 24,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
            <button
              type="button"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={() => setCollapsed(!collapsed)}
              className="app-sider-toggle"
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
              {headerContent ?? (
                <span style={{ fontSize: 18, fontWeight: 600 }}>
                  {getDefaultHeaderTitle(location.pathname) ?? ''}
                </span>
              )}
            </div>
          </div>
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow
          >
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} />
              <span style={{ marginLeft: 8 }}>{user?.name}</span>
            </div>
          </Dropdown>
        </Header>
        <Content
          style={{
            padding: 24,
            minHeight: 280,
            background: '#fff',
            overflow: disableContentScroll ? 'hidden' : undefined,
            height: disableContentScroll ? 'calc(100vh - 64px)' : undefined,
          }}
        >
          <Outlet context={{ setHeaderContent }} />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};
